import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import { Avatar, colors, IconButton, StringParser, UserTag } from '@crehana/ui';
import { usePlaybackContext } from "../context/Playback";
import { useResponsiveContext } from "../context/Responsive";
import { useSubtitleContext } from "../context/Subtitle";
import { useUIContext } from "../context/UI";
import { useTranslation } from "../hooks";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Wrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1uapm35-0"
})(["\n  width: 432px;\n  ", "\n"], function (_ref) {
  var open = _ref.open,
    visibleUi = _ref.visibleUi,
    hasVisibleSubtitles = _ref.hasVisibleSubtitles,
    hasVisibleClassNotes = _ref.hasVisibleClassNotes;
  var css = open ? "\n      visibility: visible;\n      opacity: 1;\n      transition: opacity 300ms linear, transform 300ms linear;\n    " : "\n      visibility: hidden;\n      opacity: 0;\n      transition: visibility 0s 300ms, opacity 300ms linear, transform 300ms linear;\n    ";
  if (hasVisibleSubtitles) {
    return css.concat("\n        top: 48px;\n        right: 0;\n        transform: translate(".concat(open ? '-24px' : 0, ", ").concat(visibleUi ? '76px' : 0, ");\n      "));
  }
  if (hasVisibleClassNotes) {
    return css.concat("\n        bottom: 48px;\n        left: 0;\n        transform: translate(".concat(open ? '24px' : 0, ", ").concat(visibleUi ? '-76px' : 0, ");\n      "));
  }
  return css.concat("\n      bottom: 48px;\n      left: 0;\n      transform: translate(".concat(open ? '24px' : 0, ", ").concat(visibleUi ? '-44px' : 0, ");\n    "));
});
var PublicNotes = function PublicNotes(_ref2) {
  var _currentNote$user, _currentNote$user2, _currentNote$user3, _currentNote$user4;
  var publicNotes = _ref2.publicNotes;
  var _useResponsiveContext = useResponsiveContext(),
    playerSize = _useResponsiveContext.playerSize;
  var _usePlaybackContext = usePlaybackContext(),
    videoId = _usePlaybackContext.videoId,
    currentTime = _usePlaybackContext.currentTime;
  var _useSubtitleContext = useSubtitleContext(),
    currentSubtitleLang = _useSubtitleContext.currentSubtitleLang,
    hasVisibleTextTrack = _useSubtitleContext.hasVisibleTextTrack,
    currentExternalSubtitles = _useSubtitleContext.currentExternalSubtitles;
  var _useUIContext = useUIContext(),
    classNotes = _useUIContext.classNotes,
    visibleUi = _useUIContext.visibleUi;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    languageCode = _useTranslation.languageCode;
  var hasVisibleSubtitles = (currentSubtitleLang === null || currentSubtitleLang === void 0 ? void 0 : currentSubtitleLang.value) !== 'none' && !!(hasVisibleTextTrack || currentExternalSubtitles !== null && currentExternalSubtitles !== void 0 && currentExternalSubtitles.length);
  var hasVisibleClassNotes = !!(classNotes !== null && classNotes !== void 0 && classNotes.length);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    currentNote = _useState2[0],
    setCurrentNote = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    currentNoteOpen = _useState4[0],
    setCurrentNoteOpen = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    closedIds = _useState6[0],
    setClosedIds = _useState6[1];
  var currentNoteId = currentNote === null || currentNote === void 0 ? void 0 : currentNote.originalId;
  useEffect(function () {
    setClosedIds([]);
  }, [videoId]);
  useEffect(function () {
    if (!currentNoteOpen && currentNote) {
      setTimeout(function () {
        setCurrentNote(undefined);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNoteOpen]);
  useEffect(function () {
    if (currentNote && !closedIds.includes(currentNote.originalId)) {
      setCurrentNoteOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNoteId]);
  useEffect(function () {
    var current = publicNotes.find(function (n) {
      return currentTime >= n.timePosition - 1 && currentTime <= n.timePosition + 7;
    });
    if (current && (!currentNote || current.originalId !== currentNote.originalId)) {
      setCurrentNote(current);
    } else if (!current) {
      setCurrentNoteOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(publicNotes), currentTime]);
  var onClose = useCallback(function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (currentNote) {
      setClosedIds([].concat(_toConsumableArray(closedIds), [currentNote.originalId]));
      setCurrentNoteOpen(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [currentNoteId]);
  if (!currentTime || !playerSize.matches.md) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Wrapper, {
    className: "absolute flex focus:outline-none bg-base-main-80 rounded-5 p-16 z-5 ".concat(visibleUi ? 'cursor-pointer' : 'cursor-none'),
    onClick: currentNote ? currentNote.onClick : undefined,
    open: !!currentNote && currentNoteOpen,
    visibleUi: visibleUi,
    hasVisibleSubtitles: hasVisibleSubtitles,
    hasVisibleClassNotes: hasVisibleClassNotes,
    children: [/*#__PURE__*/_jsx(Avatar, {
      src: currentNote === null || currentNote === void 0 ? void 0 : (_currentNote$user = currentNote.user) === null || _currentNote$user === void 0 ? void 0 : _currentNote$user.profilePictureUrl,
      size: 32
    }), /*#__PURE__*/_jsxs("div", {
      className: "w-full ml-8 overflow-hidden text-left",
      style: {
        maxHeight: '124px'
      },
      children: [/*#__PURE__*/_jsxs("div", {
        className: "relative flex items-center mb-4",
        children: [/*#__PURE__*/_jsx(IconButton, {
          className: "absolute",
          style: {
            top: '-8px',
            right: '-8px'
          },
          xs: true,
          onClick: onClose,
          customIcon: CloseSmall,
          customColor: colors.white,
          customSize: 12,
          "aria-label": t('CLOSE')
        }), (currentNote === null || currentNote === void 0 ? void 0 : (_currentNote$user2 = currentNote.user) === null || _currentNote$user2 === void 0 ? void 0 : _currentNote$user2.displayName) && /*#__PURE__*/_jsx("span", {
          className: "mr-8 text-white font-subtitle3",
          children: currentNote.user.displayName
        }), ((currentNote === null || currentNote === void 0 ? void 0 : (_currentNote$user3 = currentNote.user) === null || _currentNote$user3 === void 0 ? void 0 : _currentNote$user3.isStaff) || (currentNote === null || currentNote === void 0 ? void 0 : (_currentNote$user4 = currentNote.user) === null || _currentNote$user4 === void 0 ? void 0 : _currentNote$user4.isProfessor)) && /*#__PURE__*/_jsx(UserTag, {
          className: "pt-4",
          type: currentNote.user.isStaff ? 'staff' : 'prof',
          lang: languageCode
        })]
      }), (currentNote === null || currentNote === void 0 ? void 0 : currentNote.description) && /*#__PURE__*/_jsx(StringParser, {
        plainText: currentNote.description,
        textType: "body4",
        isDark: true
      })]
    })]
  });
};
export default PublicNotes;